import { Err, Ok, Result } from 'ts-res';
import { KameleoonError, KameleoonException } from 'src/kameleoonError';
import { TargetingType } from 'src/targeting/types';
import { Browser } from './browser';
import { Conversion } from './conversion';
import { CustomData } from './customData';
import { Device } from './device';
import { PageTitle } from './pageTitle';
import { PageUrl } from './pageUrl';
import { SdkLanguage } from './sdkLanguage';
import { ConditionDataType, ConditionType } from './types';
import { VisitorCode } from './visitorCode';
import { Cookie } from './cookie';
import { OperatingSystem } from './operatingSystem';
import { GeolocationCondition } from './geolocation';
import { Segment } from './segment';
import { TargetFeatureFlag } from './targetFeatureFlag';
import { PreviousPage } from './previousPage';
import { VisitedPages } from './visitedPages';
import { FirstVisit } from './firstVisit';
import { LastVisit } from './lastVisit';
import { SameDayVisits } from './sameDayVisits';
import { Visits } from './visits';
import { NewVisitor } from './newVisitor';
import { ApplicationVersion } from './applicationVersion';
import { ConversionLikelihood } from './conversionLikelihood';
import { KameleoonLogger } from 'src/logging';
import { ExclusiveExperimentCondition } from './exclusiveExperiment';
import { TargetExperimentCondition } from './targetExperiment';
import { TargetPersonalizationCondition } from './targetPersonalization';
import { IConditionFactory } from 'src/targeting/conditions/conditionFactoryInstance';

export class ConditionFactory implements IConditionFactory {
  createCondition(
    data: ConditionDataType,
  ): Result<ConditionType, KameleoonError> {
    const { targetingType } = data;

    switch (targetingType) {
      case TargetingType.CUSTOM_DATUM:
        return Ok(new CustomData(data));
      case TargetingType.EXCLUSIVE_EXPERIMENT:
        return Ok(new ExclusiveExperimentCondition(data));
      case TargetingType.TARGET_EXPERIMENT:
        return Ok(new TargetExperimentCondition(data));
      case TargetingType.TARGET_FEATURE_FLAG:
        return Ok(new TargetFeatureFlag(data));
      case TargetingType.TARGET_PERSONALIZATION:
        return Ok(new TargetPersonalizationCondition(data));
      case TargetingType.DEVICE_TYPE:
        return Ok(new Device(data));
      case TargetingType.BROWSER:
        return Ok(new Browser(data));
      case TargetingType.PAGE_URL:
        return Ok(new PageUrl(data));
      case TargetingType.PAGE_TITLE:
        return Ok(new PageTitle(data));
      case TargetingType.CONVERSIONS:
        return Ok(new Conversion(data));
      case TargetingType.VISITOR_CODE:
        return Ok(new VisitorCode(data));
      case TargetingType.SDK_LANGUAGE:
        return Ok(new SdkLanguage(data));
      case TargetingType.COOKIE:
        return Ok(new Cookie(data));
      case TargetingType.OPERATING_SYSTEM:
        return Ok(new OperatingSystem(data));
      case TargetingType.GEOLOCATION:
        return Ok(new GeolocationCondition(data));
      case TargetingType.SEGMENT:
        return Ok(new Segment(data));
      case TargetingType.APPLICATION_VERSION:
        return Ok(new ApplicationVersion(data));
      case TargetingType.PREVIOUS_PAGE:
        return Ok(new PreviousPage(data));
      case TargetingType.PAGE_VIEWS:
        return Ok(new VisitedPages(data));
      case TargetingType.LAST_VISIT:
        return Ok(new LastVisit(data));
      case TargetingType.FIRST_VISIT:
        return Ok(new FirstVisit(data));
      case TargetingType.SAME_DAY_VISITS:
        return Ok(new SameDayVisits(data));
      case TargetingType.VISITS:
        return Ok(new Visits(data));
      case TargetingType.NEW_VISITORS:
        return Ok(new NewVisitor(data));
      case TargetingType.HEAT_SLICE:
        return Ok(new ConversionLikelihood(data));
      default:
        KameleoonLogger.info`Unsupported targeted condition type found: ${targetingType}`;
        return Err(
          new KameleoonError(
            KameleoonException.TargetingCondition,
            targetingType,
          ),
        );
    }
  }
}
